import {
  EnumRichiestaDTOStato,
  EnumTagSemanticoDTOTipo,
  GetFrontRichiesteIdImmaginiIdDimensioni,
  getGetFrontRichiesteIdQueryKey,
  PostCompilazioneDTOInviato,
  RichiestaDTO,
} from '@framework/api-client';
import {
  PreviewSemanticTagGroupRender,
  SubmissionCardTabPanel,
  usePreviewSemanticTagGroups,
} from '@framework/common-web';
import { StyledHtmlText } from '@framework/engine';
import {
  SubmissionBurdenLogic,
  SubmissionLogic,
  SubmissionStateTag,
} from '@framework/frontoffice-submission';
import { QuoteActionRequiredMessages } from '@framework/frontoffice-submission/src/payments/quote/QuoteActionRequiredMessages';
import { useTranslation } from '@framework/i18n';
import { ISODate } from '@framework/ui';
import { Alert, Divider, Flex, Result, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import React, { useMemo } from 'react';
import { useInterval } from 'usehooks-ts';
import { queryClient } from '../../../core/network/queryClient';

const { Text } = Typography;

const SECONDS_30 = 30 * 1000;

export interface ISubmissionSentTabReceiptProps {
  active?: boolean;
  sentLayout?: PostCompilazioneDTOInviato | undefined;
  submission?: RichiestaDTO | undefined;
}

/**
 * Calcola se devo mostrare l'alert di richiesta inviata con successo.
 * Aggiungo una condizione in più per mostrare l'alert per almeno 25 secondi
 * per evitare che scompaia immediatamente.
 */
function computeShowAlert(submission: RichiestaDTO | undefined) {
  return (
    // Se è solo "inviata" (e non "ricevuta")
    submission?.stato === EnumRichiestaDTOStato.INVIATA ||
    // Se è stata inviata da meno di 25 secondi
    dayjs().subtract(25, 'seconds').isBefore(dayjs(submission?.dataInvio))
  );
}

/**
 * Tab della ricevuta di invio richiesta.
 */
export function SubmissionSentTabReceipt(
  props: ISubmissionSentTabReceiptProps,
) {
  const { active, sentLayout, submission } = props;
  const { t } = useTranslation();

  // Intervallo per il polling per aggiornare lo stato della richiesta
  // in attesa che venga protocollata. Aggiorno la richiesta finché non
  // è nello stato "RICEVUTA"
  useInterval(
    () => {
      if (!submission?.id) return;
      queryClient.invalidateQueries(
        getGetFrontRichiesteIdQueryKey(submission.id),
      );
    },
    submission?.stato !== EnumRichiestaDTOStato.RICEVUTA ? SECONDS_30 : null,
  );

  // Indica se devo o meno mostrare l'alert di richiesta inviata con successo
  const [showAlert, setShowAlert] = React.useState<boolean>(
    computeShowAlert(submission),
  );

  // Ricalcolo l'alert ogni 30 secondi
  useInterval(() => {
    setShowAlert(computeShowAlert(submission));
  }, SECONDS_30);

  const submissionCodes = SubmissionLogic.getCodes(submission);

  const semanticFields = useMemo(
    () =>
      // prendiamo tutti i campi semantici visualizzabili e li ordiniamo per titolo
      submission?.campiSemantici
        ?.filter(field => field.tagSemantico?.flVisualizzabile)
        ?.sort(
          (t1, t2) =>
            (t1.tagSemantico?.titolo ?? '').localeCompare(
              t2.tagSemantico?.titolo ?? '',
            ) ?? 0,
        ) ?? [],
    [submission?.campiSemantici],
  );

  const imageSemanticFields = usePreviewSemanticTagGroups(
    semanticFields.filter(
      field => field.tagSemantico?.tipo === EnumTagSemanticoDTOTipo.IMAGE,
    ),
  );

  if (!active) return null;

  return (
    <SubmissionCardTabPanel
      active={active}
      label={t(
        'submission-sent-tab-recipt.panel-aria',
        'Tab Ricevuta invio della richiesta',
      )}
    >
      {showAlert && (
        <>
          <Result
            status="success"
            title={
              sentLayout?.messaggio ??
              t(
                'submission.richiesta-inviata-con-successo',
                'Richiesta inviata con successo!',
              )
            }
            subTitle={
              sentLayout?.testo ? (
                <StyledHtmlText
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(sentLayout?.testo),
                  }}
                />
              ) : (
                t(
                  'submission.richiesta-inviata-correttamente-da-prendere-in-carico',
                  'La richiesta è stata inviata correttamente e sarà presa in carico a breve.',
                )
              )
            }
          />
          <Divider />
        </>
      )}
      {SubmissionBurdenLogic.hasPendingQuotes(submission?.statoPreventivi) && (
        <Alert
          style={{ marginBottom: '24px' }}
          showIcon
          type="warning"
          message={<QuoteActionRequiredMessages submission={submission} />}
          description={t(
            'submission.vai-ai-pagamenti',
            'Vai alla sezione "Pagamenti" per visualizzare e gestire il preventivo',
          )}
        />
      )}
      <Flex vertical gap={6}>
        <Space>
          <Text>{t('submission.stato-richiesta', 'Stato richiesta:')}</Text>
          <SubmissionStateTag submission={submission} />
        </Space>
        {submission?.oggetto && (
          <Space>
            <Text style={{ fontWeight: 'normal' }}>
              {t('submission.oggetto-richiesta', 'Oggetto:')}{' '}
            </Text>
            <Text strong>{submission?.oggetto}</Text>
          </Space>
        )}
        {submissionCodes.submissionCode && (
          <Space>
            <Text>{t('submission.codice-richiesta', 'Codice richiesta:')}</Text>
            <Text strong>{submissionCodes.submissionCode}</Text>
          </Space>
        )}
        {submission?.flVisualizzaNumeroProcedimento &&
          submissionCodes.procedureNumber && (
            <Space>
              <Text>
                {t('submission.numero-procedimento', 'Numero procedimento:')}
              </Text>
              <Text strong>{submissionCodes.procedureNumber}</Text>
            </Space>
          )}
        <Space>
          <Text>{t('submission.data-invio', 'Data invio:')}</Text>
          <Text strong>
            <ISODate date={submission?.dataInvio} format="DD/MM/YYYY HH:mm" />
          </Text>
        </Space>
        {submissionCodes.protocolNumber && (
          <>
            <Space>
              <Text>
                {t('submission.numero-protocollo', 'Numero protocollo:')}
              </Text>
              <Text strong>{submissionCodes.protocolNumber}</Text>
            </Space>
            <Space>
              <Text>{t('submission.data-protocollo', 'Data protocollo:')}</Text>
              <Text strong>
                <ISODate date={submission?.dataProtocollo} />
              </Text>
            </Space>
          </>
        )}
        {semanticFields
          .filter(
            field => field.tagSemantico?.tipo !== EnumTagSemanticoDTOTipo.IMAGE,
          )
          .map(field => {
            if (field.tagSemantico?.tipo === EnumTagSemanticoDTOTipo.DATE) {
              return (
                <Space key={field.tagSemantico?.id}>
                  <Text>{field.tagSemantico?.titolo}: </Text>
                  <Text strong>
                    <ISODate
                      date={field.valore as Date | string}
                      format="DD/MM/YYYY HH:mm"
                    />
                  </Text>
                </Space>
              );
            }
            return (
              <Space key={field.tagSemantico?.id}>
                <Text>{field.tagSemantico?.titolo}: </Text>
                <Text strong>{field.valore as any}</Text>
              </Space>
            );
          })}
        {imageSemanticFields.map(field => {
          return (
            <Space direction="vertical" key={field.id}>
              <Text>{field.tag}: </Text>
              <PreviewSemanticTagGroupRender
                images={field.images}
                submissionId={submission?.id ?? ''}
                borderRadius={8}
                objectFit="cover"
                width={120}
                height={120}
                dimension={GetFrontRichiesteIdImmaginiIdDimensioni.ANTEPRIMA}
              />
            </Space>
          );
        })}
      </Flex>
    </SubmissionCardTabPanel>
  );
}
